import { useEffect, useState } from "react";

import { useConfirmMaterialList, useConfirmPreview, useMaterialLists } from "@/api/queries";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Field, Select } from "@/components/Input";
import { EmptyState, Skeleton } from "@/components/States";
import { useToast } from "@/components/toast-context";
import { formatQuantity, formatUnit, pluralize } from "@/lib/format";

/**
 * Confirmer des lignes de liste en besoins (V1.6-02).
 *
 * Le dialogue **montre d'abord ce qui sera créé**, ligne par ligne, avec des
 * cases décochables. Personne ne doit découvrir après coup qu'il vient de créer
 * trente besoins — c'est la raison d'être de l'aperçu, et c'est pour ça qu'il
 * est servi par une route de lecture séparée de celle qui écrit.
 */
export function ConfirmFromListDialog({
  open,
  onOpenChange,
  projectId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}) {
  const toast = useToast();
  const [listId, setListId] = useState("");
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const { data: lists } = useMaterialLists({ project_id: projectId, limit: 50 }, { enabled: open });
  const { data: preview, isLoading } = useConfirmPreview(projectId, listId || undefined);
  const confirm = useConfirmMaterialList();

  useEffect(() => {
    setExcluded(new Set());
  }, [listId]);

  const lines = preview?.lines ?? [];
  const kept = lines.filter((line) => !excluded.has(line.line_id));

  function toggle(lineId: string) {
    setExcluded((current) => {
      const next = new Set(current);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }

  async function submit() {
    if (!listId || kept.length === 0) return;
    try {
      await confirm.mutateAsync({
        projectId,
        listId,
        // Toujours explicite, même quand tout est retenu : « toutes » doit être
        // une décision, pas un défaut silencieux.
        line_ids: kept.map((line) => line.line_id),
      });
      toast.success("Besoins confirmés", pluralize(kept.length, "ligne"));
      onOpenChange(false);
      setListId("");
    } catch (error) {
      toast.error("Besoins non confirmés", String(error));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setListId("");
          setExcluded(new Set());
        }
        onOpenChange(next);
      }}
      title="Confirmer les besoins"
      description="Une liste est une intention. Un besoin confirmé peut réserver du stock."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!listId || kept.length === 0 || confirm.isPending}
          >
            {confirm.isPending
              ? "Confirmation…"
              : `Confirmer ${pluralize(kept.length, "ligne")}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Liste de matériel">
          <Select
            value={listId}
            onChange={(event) => setListId(event.target.value)}
            aria-label="Liste de matériel"
          >
            <option value="">Choisir une liste…</option>
            {lists?.items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.code} — {item.name}
              </option>
            ))}
          </Select>
        </Field>

        {listId && isLoading && <Skeleton className="h-24" />}

        {listId && !isLoading && lines.length === 0 && (
          <EmptyState title="Rien à confirmer" description="Cette liste n'a aucune ligne active." />
        )}

        {lines.length > 0 && (
          <div
            className="max-h-64 overflow-y-auto rounded-md border border-border"
            aria-label="Ce qui sera créé"
          >
            <ul className="divide-y divide-border/60">
              {lines.map((line) => (
                <li key={line.line_id} className="flex items-center gap-3 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={!excluded.has(line.line_id)}
                    onChange={() => toggle(line.line_id)}
                    aria-label={`Confirmer ${line.description}`}
                    className="size-4 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] text-ink">{line.description}</p>
                    <p className="text-[11px] text-ink-subtle">
                      {formatQuantity(line.quantity)} {formatUnit(line.unit)}
                      {line.product_id === null ? " · hors catalogue" : ""}
                      {line.already_required ? " · déjà demandé, la quantité sera corrigée" : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  );
}
